import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import {
  ApiError,
  changePassword,
  updateProfile,
  uploadAppFile,
} from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import PasswordField from '../components/PasswordField';

const MAX_AVATAR_MB = 10; // matches the file-service default cap
const MAX_AVATAR_BYTES = MAX_AVATAR_MB * 1024 * 1024;

// Common US time zones (stored as IANA-style strings, matching the iOS picker).
const TIME_ZONES = [
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'US/Alaska',
  'US/Hawaii',
  'US/Arizona',
];

function initialOf(name?: string | null, email?: string | null): string {
  const s = (name || email || '?').trim();
  return s ? s.charAt(0).toUpperCase() : '?';
}

/** Turn an upload failure into a clear, friendly message (esp. the 413 too-large case). */
function avatarErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 413) {
      // Prefer the server's stated cap (it's configurable) if present.
      const m = /(\d+)\s*MB/i.exec(err.message);
      const limit = m ? `${m[1]} MB` : `${MAX_AVATAR_MB} MB`;
      return `That photo is too large — please choose an image under ${limit}.`;
    }
    return err.message;
  }
  return err instanceof Error
    ? err.message
    : 'Could not update your photo. Please try again.';
}

/**
 * Profile page (Phase 8 / task #214).
 *
 * Two zones:
 *   1. Account info — display name + email, read-only. (Editing display
 *      name needs a Gateway-side `aquastarUserUpdate` handler that
 *      doesn't exist yet; tracked as a follow-up.)
 *   2. Change password — current + new + confirm, posts to
 *      `/auth/change-password`. Inline success state, surface
 *      Gateway-issued error messages verbatim (the Gateway already
 *      composes user-facing copy for INVALID_PASSWORD / WEAK_PASSWORD).
 *
 * Plus a Sign out button at the bottom — clears the JWT and routes to
 * /login. Cleanest place for it; the AppHeader has its own profile
 * chip but no logout in v0.
 *
 * Future ideas (deferred): edit display name, MFA enrollment, manage
 * follower invites you've issued, GDPR data export.
 */
export function Profile() {
  const navigate = useNavigate();
  const { user, signOut, refresh } = useAuth();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Contact details (optional). Seeded from /auth/me; re-seeded after a refresh.
  const [contact, setContact] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    phone: '',
    timeZone: '',
  });
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactSaved, setContactSaved] = useState(false);

  useEffect(() => {
    setContact({
      addressLine1: user?.addressLine1 ?? '',
      addressLine2: user?.addressLine2 ?? '',
      city: user?.city ?? '',
      state: user?.state ?? '',
      postalCode: user?.postalCode ?? '',
      phone: user?.phone ?? '',
      timeZone: user?.timeZone ?? '',
    });
  }, [user]);

  const onContactField =
    (key: keyof typeof contact) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setContactSaved(false);
      const value = e.target.value;
      setContact((c) => ({ ...c, [key]: value }));
    };

  async function onSaveContact(ev: FormEvent) {
    ev.preventDefault();
    setContactError(null);
    setContactSaved(false);
    setContactBusy(true);
    try {
      await updateProfile({
        addressLine1: contact.addressLine1,
        addressLine2: contact.addressLine2,
        city: contact.city,
        state: contact.state,
        postalCode: contact.postalCode,
        phone: contact.phone,
        timeZone: contact.timeZone,
      });
      await refresh();
      setContactSaved(true);
    } catch (err) {
      setContactError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not save your details. Please try again.',
      );
    } finally {
      setContactBusy(false);
    }
  }

  function validate(): string | null {
    if (!currentPassword) return 'Please enter your current password.';
    if (newPassword.length < 8)
      return 'New password must be at least 8 characters.';
    if (newPassword === currentPassword)
      return 'New password must be different from your current password.';
    if (newPassword !== confirmPassword) return "New passwords don't match.";
    return null;
  }

  async function onSubmitPasswordChange(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setSuccess(false);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Could not change password. Please try again.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleSignOut() {
    signOut();
    navigate('/login', { replace: true });
  }

  async function onPickAvatar(ev: ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setAvatarError(null);
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please choose an image file (JPG, PNG, …).');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError(
        `That photo is too large — please choose an image under ${MAX_AVATAR_MB} MB.`,
      );
      return;
    }
    setAvatarBusy(true);
    try {
      const uploaded = await uploadAppFile(file);
      await updateProfile({ image: uploaded.url });
      await refresh();
    } catch (err) {
      setAvatarError(avatarErrorMessage(err));
    } finally {
      setAvatarBusy(false);
    }
  }

  async function onRemoveAvatar() {
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      await updateProfile({ image: null });
      await refresh();
    } catch (err) {
      setAvatarError(avatarErrorMessage(err));
    } finally {
      setAvatarBusy(false);
    }
  }

  return (
    <>
      <AppHeader />
      <main className="container py-4" style={{ maxWidth: 720 }}>
        <p className="mb-3">
          <Link to="/dashboard">← Back to my pumps</Link>
        </p>
        <h2 className="h3 mb-4" style={{ color: 'var(--aqua-text-strong)' }}>
          Your profile
        </h2>

        {/* Account info */}
        <div className="card mb-4">
          <div className="card-body">
            <h3 className="h6 text-muted mb-3">Account</h3>

            {/* Profile photo */}
            <div className="d-flex align-items-center gap-3 mb-4">
              {user?.image ? (
                <img
                  src={user.image}
                  alt="Profile"
                  width={72}
                  height={72}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '1px solid var(--aqua-border, #dee2e6)',
                  }}
                />
              ) : (
                <div
                  aria-hidden
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: 'var(--aqua-accent, #0ea5b7)',
                    color: '#fff',
                    fontSize: 28,
                    fontWeight: 600,
                  }}
                >
                  {initialOf(user?.displayName || user?.givenName, user?.email)}
                </div>
              )}
              <div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={avatarBusy}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {avatarBusy
                      ? 'Uploading…'
                      : user?.image
                        ? 'Change photo'
                        : 'Add photo'}
                  </button>
                  {user?.image && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0"
                      disabled={avatarBusy}
                      onClick={onRemoveAvatar}
                    >
                      Remove
                    </button>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={onPickAvatar}
                  />
                </div>
                {avatarError ? (
                  <div className="text-danger small mt-1">{avatarError}</div>
                ) : (
                  <div className="form-text mt-1">JPG or PNG, up to 10 MB.</div>
                )}
              </div>
            </div>

            <dl className="row mb-0">
              <dt className="col-sm-4 text-muted small">Name</dt>
              <dd className="col-sm-8">
                {user?.displayName || user?.givenName || (
                  <span className="text-muted fst-italic">Not set</span>
                )}
              </dd>

              <dt className="col-sm-4 text-muted small">Email</dt>
              <dd className="col-sm-8">{user?.email || '—'}</dd>

              {user?.createdAt && (
                <>
                  <dt className="col-sm-4 text-muted small">Member since</dt>
                  <dd className="col-sm-8">
                    {new Date(user.createdAt).toLocaleDateString(
                      undefined,
                      { year: 'numeric', month: 'long', day: 'numeric' },
                    )}
                  </dd>
                </>
              )}
            </dl>
          </div>
        </div>

        {/* Contact details (optional) */}
        <div className="card mb-4">
          <div className="card-body">
            <h3 className="h6 text-muted mb-3">Contact details</h3>
            <form onSubmit={onSaveContact} noValidate>
              <div className="mb-3">
                <label className="form-label small text-muted">
                  Address line 1
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={contact.addressLine1}
                  onChange={onContactField('addressLine1')}
                  autoComplete="address-line1"
                />
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted">
                  Address line 2
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={contact.addressLine2}
                  onChange={onContactField('addressLine2')}
                  autoComplete="address-line2"
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-sm-6">
                  <label className="form-label small text-muted">City</label>
                  <input
                    type="text"
                    className="form-control"
                    value={contact.city}
                    onChange={onContactField('city')}
                    autoComplete="address-level2"
                  />
                </div>
                <div className="col-sm-3">
                  <label className="form-label small text-muted">State</label>
                  <input
                    type="text"
                    className="form-control"
                    value={contact.state}
                    onChange={onContactField('state')}
                    autoComplete="address-level1"
                  />
                </div>
                <div className="col-sm-3">
                  <label className="form-label small text-muted">ZIP</label>
                  <input
                    type="text"
                    className="form-control"
                    value={contact.postalCode}
                    onChange={onContactField('postalCode')}
                    autoComplete="postal-code"
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-sm-6">
                  <label className="form-label small text-muted">Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    value={contact.phone}
                    onChange={onContactField('phone')}
                    autoComplete="tel"
                  />
                </div>
                <div className="col-sm-6">
                  <label className="form-label small text-muted">
                    Time zone
                  </label>
                  <select
                    className="form-select"
                    value={contact.timeZone}
                    onChange={onContactField('timeZone')}
                  >
                    <option value="">— Select —</option>
                    {!TIME_ZONES.includes(contact.timeZone) &&
                      contact.timeZone !== '' && (
                        <option value={contact.timeZone}>
                          {contact.timeZone}
                        </option>
                      )}
                    {TIME_ZONES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {contactError && (
                <div className="alert alert-danger" role="alert">
                  {contactError}
                </div>
              )}
              {contactSaved && (
                <div className="alert alert-success" role="status">
                  Details saved.
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={contactBusy}
              >
                {contactBusy ? 'Saving…' : 'Save details'}
              </button>
            </form>
          </div>
        </div>

        {/* Change password */}
        <div className="card mb-4">
          <div className="card-body">
            <h3 className="h6 text-muted mb-3">Change password</h3>
            <form onSubmit={onSubmitPasswordChange} noValidate>
              <div className="mb-3">
                <label className="form-label small text-muted">
                  Current password
                </label>
                <PasswordField
                  className="form-control"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted">
                  New password
                </label>
                <PasswordField
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <div className="form-text">At least 8 characters.</div>
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted">
                  Confirm new password
                </label>
                <PasswordField
                  className="form-control"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </div>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              {success && (
                <div className="alert alert-success" role="status">
                  Password updated.
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  submitting ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword
                }
              >
                {submitting ? 'Saving…' : 'Change password'}
              </button>
            </form>
          </div>
        </div>

        {/* Sign out */}
        <div className="card">
          <div className="card-body">
            <h3 className="h6 text-muted mb-3">Sign out</h3>
            <p className="small text-muted mb-3">
              Sign out of AquaStar on this browser. You can sign back in
              anytime.
            </p>
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleSignOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </main>
    </>
  );
}
